import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { Option, Record as EffectRecord } from 'effect'

import type { ApprovalAnswer, ChatEvent } from '../domain/chatEvents'
import { renamePendingKey } from '../domain/pendingId'
import {
  answerChatApproval,
  applyChatEvents,
  clearChatTurnStop,
  closeChatTurnStream,
  EMPTY_SESSION_CHAT,
  failChatApprovalAnswer,
  rejectChatApprovalAnswer,
  renameChatTurn,
  requestChatTurnStop,
  resetChatTurn,
  type SessionChat,
  startChatApprovalAnswer,
  startChatTurn,
} from '../domain/sessionChat'
import { sessionIdAssigned } from './pendingId'

// エージェントとの会話の表示の状態。会話の移り方は domain/sessionChat.ts で決め、この slice はセッションごとに持つだけにする。
// 読み書きするのはウィジェットの中の部品だけなので、アプリ全体の redux の store ではなく、AgentWidget の useReducer に持つ（ADR-104）。
// reducer は Redux Toolkit の slice で書き、ウィジェットの外が読むようになったら、そのまま store に移せるようにする。
// 状態は store の直列化の検査に掛からないよう、素のオブジェクトと配列で持つ。変わったセッションだけを新しいオブジェクトにする

// セッションごとの会話。キーは agentSessionId（作る前は仮の ID。domain/pendingId.ts）
export type AgentChatState = Readonly<Record<string, SessionChat>>

const updateSession = (
  state: AgentChatState,
  agentSessionId: string,
  update: (session: SessionChat) => SessionChat,
): AgentChatState =>
  EffectRecord.set(
    state,
    agentSessionId,
    update(Option.getOrElse(EffectRecord.get(state, agentSessionId), () => EMPTY_SESSION_CHAT)),
  )

const INITIAL_AGENT_CHAT_STATE: AgentChatState = {}

// case reducer は Immer の draft を書き換えず、純粋な関数で作った新しい状態を返す
export const agentChatSlice = createSlice({
  name: 'agentChat',
  initialState: INITIAL_AGENT_CHAT_STATE,
  reducers: {
    turnStarted: (
      state,
      action: PayloadAction<{ agentSessionId: string; turnId: string; prompt: string; startedAt: string }>,
    ) => updateSession(state, action.payload.agentSessionId, (session) => startChatTurn(session, action.payload)),
    turnIdAssigned: (state, action: PayloadAction<{ agentSessionId: string; pendingTurnId: string; turnId: string }>) =>
      updateSession(state, action.payload.agentSessionId, (session) =>
        renameChatTurn(session, action.payload.pendingTurnId, action.payload.turnId),
      ),
    chatEventsReceived: (
      state,
      action: PayloadAction<{ agentSessionId: string; turnId: string; events: readonly ChatEvent[] }>,
    ) =>
      updateSession(state, action.payload.agentSessionId, (session) =>
        applyChatEvents(session, action.payload.turnId, action.payload.events),
      ),
    turnStopRequested: (state, action: PayloadAction<{ agentSessionId: string; turnId: string }>) =>
      updateSession(state, action.payload.agentSessionId, (session) =>
        requestChatTurnStop(session, action.payload.turnId),
      ),
    turnStopFailed: (state, action: PayloadAction<{ agentSessionId: string; turnId: string }>) =>
      updateSession(state, action.payload.agentSessionId, (session) =>
        clearChatTurnStop(session, action.payload.turnId),
      ),
    turnReset: (state, action: PayloadAction<{ agentSessionId: string; turnId: string }>) =>
      updateSession(state, action.payload.agentSessionId, (session) => resetChatTurn(session, action.payload.turnId)),
    approvalAnswerSent: (state, action: PayloadAction<{ agentSessionId: string; approvalId: string }>) =>
      updateSession(state, action.payload.agentSessionId, (session) =>
        startChatApprovalAnswer(session, action.payload.approvalId),
      ),
    approvalAnswerFailed: (state, action: PayloadAction<{ agentSessionId: string; approvalId: string }>) =>
      updateSession(state, action.payload.agentSessionId, (session) =>
        failChatApprovalAnswer(session, action.payload.approvalId),
      ),
    approvalAnswerRejected: (state, action: PayloadAction<{ agentSessionId: string; approvalId: string }>) =>
      updateSession(state, action.payload.agentSessionId, (session) =>
        rejectChatApprovalAnswer(session, action.payload.approvalId),
      ),
    approvalAnswered: (
      state,
      action: PayloadAction<{ agentSessionId: string; approvalId: string; answer: ApprovalAnswer }>,
    ) =>
      updateSession(state, action.payload.agentSessionId, (session) =>
        answerChatApproval(session, action.payload.approvalId, action.payload.answer),
      ),
    turnStreamClosed: (state, action: PayloadAction<{ agentSessionId: string; turnId: string; message: string }>) =>
      updateSession(state, action.payload.agentSessionId, (session) =>
        closeChatTurnStream(session, action.payload.turnId, action.payload.message),
      ),
  },
  extraReducers: (builder) => {
    builder.addCase(sessionIdAssigned, (state, action) =>
      renamePendingKey(state, action.payload.pendingSessionId, action.payload.agentSessionId),
    )
  },
})

export const {
  turnStarted,
  turnIdAssigned,
  chatEventsReceived,
  turnStopRequested,
  turnStopFailed,
  approvalAnswerSent,
  approvalAnswerFailed,
  approvalAnswerRejected,
  approvalAnswered,
  turnReset,
  turnStreamClosed,
} = agentChatSlice.actions
